import { useMemo, useState, type FC } from 'react';
import type { HeroDetailedProps } from './types';

import styled, { css } from 'styled-components';
import {
  COLOR_ORANGE,
  COLOR_YELLOW,
  defaultWebValue,
  filterBackgroundColor,
  sanitizeRichText,
  generateAccessKey
} from 'shared/helpers/index';
import { useBook, useSales, useWebsiteEditor } from 'shared/hooks/index';

import { Button, Wrapper, Mockup, Download } from '../Common';
import useIsEmpty from 'shared/hooks/useIsEmpty';
import { Image as ImageComp } from 'shared/components/Common';
import { ConditionalImage } from '../ConditionalImage';

import SvgIcons from 'shared/components/Common/IconsHeroDetailed';
import useGeneralActions from '../hooks/useGeneralActions';
import ArrowsSvg from 'shared/components/Common/ArrowsSvg';

const HeroDetailed = styled.div`
  position: relative;
  overflow-x: hidden;
  min-height: 693px;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
  flex-direction: column;

  `}
`;

const StyledWrapper = styled((props) => <Wrapper {...props} />)`
  display: flex;
  justify-content: space-between;
  padding: 70px 0 70px 0px;
  max-width: 100%;
  gap: 50px;

  @media screen and (max-width: 710px) {
    gap: 40px;
    flex-direction: column-reverse;
  }
`;

const TitleWrapper = styled.div`
  width: 655px;
  position: relative;
  z-index: 17;
  margin-top: -19px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 472px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 100%;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 20px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    padding: 0 15px;
    `}
`;

const Title = styled.h1<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $largeHeadlineFont } }) => $largeHeadlineFont};
  line-height: 1.25em;
  color: #fff;
  margin-bottom: 34px;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const Description = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  margin-bottom: 80px;
  line-height: 1.35em;
  color: #fff;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    margin-bottom: 59px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    margin-bottom: 40px;
    `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const ButtonInner = styled.div`
  position: relative;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 100%;
  `}
`;

const BtnInner = styled.div`
  width: fit-content;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
  `}
`;

const buttonStyles = css`
  max-width: 385px;
  display: inline-block;
  padding-left: 50px;
  padding-right: 50px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
      max-width: 345px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
      max-width: 380px;
      padding: 19px 32px;
    `}
`;

const DownloadStyled = styled(Download)`
  ${buttonStyles}
`;

const StyledButton = styled(Button)`
  ${buttonStyles}
`;

const ArrowsSvgStyled = styled(ArrowsSvg)`
  position: absolute;
  right: -156px;
  width: 130px;
  top: -40px;
  opacity: 0.4;
  z-index: -1;

  @media screen and (max-width: 900px) {
    display: none;
  }
`;

const RelativeWrapper = styled.div`
  position: relative;
  z-index: 12;
  width: 704px;
  height: 704px;
  display: flex;
  justify-content: center;
  align-items: center;
  right: -4%;

  @media screen and (max-width: 991px) {
    width: 584px;
    height: 584px;
  }

  @media screen and (max-width: 710px) {
    left: 0;
    right: 0;
    margin: 0 auto;
    width: 100%;
    height: 584px;
  }

  @media screen and (max-width: 480px) {
    width: 100%;
    margin: 0 auto;
  }
`;

const SvgBg = styled.svg`
  width: 109%;
  height: 109%;
  position: absolute;
  top: -4.5%;
  left: -4.5%;
  right: 0;
  margin: 0 auto;
  z-index: 1;

  .cls-1 {
    fill: none;
    stroke: #fff;
    stroke-width: 3px;
  }

  .cls-3 {
    fill: #fff;
  }

  @media screen and (max-width: 710px) {
    top: 0px;
    left: 0px;
    right: 0;
    width: 100%;
    height: 100%;
  }
`;

const StyledMockup = styled((props) => <Mockup {...props} />)`
  width: 506px;
  height: auto;
  object-fit: fill;
  z-index: 2;
  position: relative;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 438px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    width: 340px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 370 &&
    `
    width: 270px;
  `}
`;

const BenefitWrapper = styled.div`
  position: relative;
`;

const Benefits = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  position: relative;
  z-index: 24;
  border-top: 1px solid rgb(255 255 255 / 31%);
  padding: 50px 0 90px;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 100%;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    flex-direction: column;
    bottom: auto;
    padding-bottom: 80px;
    padding-top: 50px;
    `}
`;

const BenefitItem = styled.div`
  width: 31.5%;
  padding: 24px;
  border-radius: 17px;
  background: #fff;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.08);
  display: flex;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    flex-direction: column;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 32.5%;
    padding: 18px 18px 20px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    flex-direction: row;
    width: 100%;
    padding: 18px 15px;
    border-radius: 12px;

    &:not(:last-child) {
      margin-bottom: 10px;
    }
    `}
`;

const StyledSvgIcons = styled(SvgIcons)`
  width: 62px;
  height: 62px;
  margin-right: 18px;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
      width: 52px;
      height: 52px;
      margin-bottom: 8px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    width: 52px;
    height: 52px;
    margin-bottom: 8px;
  `}
`;

const BenefitTxtInner = styled.div`
  width: 78%;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
      width: 100%;
    `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
      width: 80%;
    `}
`;

const TitleBenefit = styled.h3<{ isEmpty?: boolean; editable?: boolean }>`
  margin-bottom: 12px;
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  font-weight: 500;
  line-height: 1.35em;

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    margin-bottom: 4px;
    `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const TextBenefit = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $tinyTextFont } }) => $tinyTextFont};
  line-height: 1.3em;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const WhiteInner = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  z-index: 3;
  overflow: hidden;
`;

const RelativeWhite = styled.div<{ isWhite?: boolean }>`
  position: relative;
  width: 1920px;
  height: 100%;
  margin: 0 auto;
  opacity: ${(props) => (props.isWhite ? 0.3 : 1)};

  .hero-white-v5 {
    border-radius: 50%;
    filter: blur(128px);
    background: ${(props) => (props.isWhite ? '#000' : '#fff')};
    position: absolute;
    margin: 0 auto;
    left: 0;
    right: 0;
  }

  .hero-white-v5_one {
    width: 810px;
    height: 810px;
    opacity: 0.3;
    bottom: -20%;
    right: -44%;
  }

  .hero-white-v5_two {
    width: 522px;
    height: 522px;
    opacity: 0.3;
    background: ${(props) => (props.isWhite ? '#000' : '#fff')};
    right: -38%;
    bottom: 25%;
  }

  .hero-white-v5_three {
    width: 609px;
    height: 609px;
    opacity: 0.5;
    background: ${(props) => (props.isWhite ? '#000' : '#fff')};
    right: -28%;
    bottom: 30%;
  }

  .hero-white-v5_four {
    width: 1307px;
    height: 1307px;
    opacity: 0.33;
    background: ${(props) => (props.isWhite ? '#000' : '#fff')};
    left: -52%;
    bottom: -74%;
  }

  .hero-white-v5_five {
    width: 322px;
    height: 322px;
    opacity: 0.3;
    background: ${(props) => (props.isWhite ? '#000' : '#fff')};
    left: -68%;
    top: 1%;
  }

  .hero-white-v5_six {
    width: 322px;
    height: 322px;
    opacity: 0.24;
    background: ${(props) => (props.isWhite ? '#000' : '#fff')};
    right: -78%;
    top: 1%;
  }

  @media screen and (max-width: 575px) {
    .hero-white-v5_three {
      width: 609px;
      height: 609px;
      opacity: 0.35;
      background: #fff;
      right: 22%;
      bottom: auto;
      top: -3%;
    }

    .hero-white-v5_six {
      width: 322px;
      height: 322px;
      opacity: 0.54;
      background: #fff;
      left: 0;
      right: auto;
      top: 21%;
    }

    .hero-white-v5_two {
      width: 382px;
      height: 382px;
      opacity: 0.5;
      background: #fff;
      right: auto;
      bottom: 15%;
    }
  }
`;

const ColoredBg = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  border-top: 1px solid rgb(255 255 255 / 16%);

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    height: 100%;
    `}
`;

const ImgInner = styled.div`
  width: 100%;
  position: relative;
  z-index: 36;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const imageStyles = css`
  position: relative;
  overflow: hidden;
`;

const HoverableDropzoneStyled = styled(ConditionalImage)<{ width: number; height: number }>`
  max-width: 670px;
  max-height: 830px;
  height: 685px;
  width: 513px;

  @media screen and (max-width: 1268px) {
    width: 92% !important;
    height: auto !important;
    aspect-ratio: ${({ width, height }) =>
      width && height ? `${width} / ${height}` : '513 / 685'};
    object-fit: cover;
    margin: 0 auto;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  @media screen and (max-width: 768px) {
    width: 82% !important;
  }

  @media screen and (max-width: 545px) {
    width: 93% !important;
  }
`;

const BookMediaImg = styled(ImageComp)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
`;

export const V5: FC<HeroDetailedProps> = (props) => {
  const {
    mockup,
    cover_thumbnail,
    title = defaultWebValue,
    content = defaultWebValue,
    button = defaultWebValue,
    secondaryColor = COLOR_YELLOW,
    customBackgroundColor,
    textColor,
    benefits = [],
    meta,
    hidden,
    order
  } = props;

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();
  const { isCoverDisabled, pages, bookMedia } = useBook();
  const { pagesThumbnails, updateImageMeta, purchaseElementId, type, isDashboard } = useSales();
  const { handleUploadAction } = useGeneralActions(props);
  const [resize, setResize] = useState({
    width: meta?.['width'] ?? 0,
    height: meta?.['height'] ?? 0
  });

  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { isEmpty: isContentEmpty } = useIsEmpty(meta, 'content');

  const [isLoading, setIsLoading] = useState(false);

  const { checkBenefits: checkTitleBenefits } = useIsEmpty(meta, 'title', true);
  const { checkBenefits: checkContentBenefits } = useIsEmpty(meta, 'content', true);

  const coverThumbnail = useMemo(() => {
    if (isCoverDisabled && (pages.length > 0 || pagesThumbnails.length > 0)) {
      return pagesThumbnails[0] || pages[0]?.image_preview;
    }
    return cover_thumbnail;
  }, [isCoverDisabled, pages, pagesThumbnails, cover_thumbnail]);

  const bookMockup = useMemo(() => {
    if (meta['image'] && !meta['image_placeholder']) {
      return meta['image'];
    }

    const media = bookMedia?.find((media) => media.name === 'book_mockup');
    return media?.url;
  }, [bookMedia, meta]);

  const resolvedBackgroundColor = useMemo(() => {
    return customBackgroundColor || secondaryColor;
  }, [customBackgroundColor, secondaryColor]);

  // Accessibility improvements
  const titleAccessKey = useMemo(() => generateAccessKey('ht', order), [order]);
  const contentAccessKey = useMemo(() => generateAccessKey('hc', order), [order]);
  const buttonAccessKey = useMemo(() => generateAccessKey('hb', order), [order]);
  const skipLinkId = useMemo(() => `skip-to-hero-${meta['accessorKey'] || 'hero'}`, [meta]);

  const buttonType = useMemo(() => {
    return meta['url']
      ? 'external link'
      : type === 'delivery'
        ? 'delivery action'
        : 'purchase button';
  }, [meta, type]);

  const handleEditError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Hero edit error: ${error instanceof Error ? error.message : 'Edit action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleModalError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Button action error: ${error instanceof Error ? error.message : 'Action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleUploadError = (error: Error | unknown) => {
    // Announce upload error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Image upload error: ${error instanceof Error ? error.message : 'Upload failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleUpload = async (file: File) => {
    try {
      setIsLoading(true);
      await handleUploadAction(file, setResize, 'book_mockup');
      setIsLoading(false);
    } catch (error) {
      handleUploadError(error);
      setIsLoading(false);
    }
  };

  return (
    <HeroDetailed
      className={containerClassname}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1
      }}
      aria-label="Hero section with circular design and separated benefits"
      role="region">
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to hero detailed content</a>
      </span>

      <StyledWrapper id={skipLinkId}>
        <TitleWrapper id="HeadlineSection">
          <Title
            id={title.id}
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(title.val, isDashboard)
            }}
            style={{
              color: filterBackgroundColor(resolvedBackgroundColor, textColor)
            }}
            isEmpty={isTitleEmpty}
            {...(isDashboard
              ? {
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    try {
                      updateActiveElement(e);
                    } catch (error) {
                      handleEditError(error);
                    }
                  },
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta['accessorKey'],
                  'data-accessorkey': 'title',
                  'data-placeholder': '+',
                  editable: true
                }
              : {
                  'aria-label':
                    isTitleEmpty || !title.val?.trim() ? 'Hero section main title' : undefined,
                  accessKey: titleAccessKey,
                  title: `Press Alt+${titleAccessKey} to focus this hero title`,
                  tabIndex: 0
                })}
          />

          <Description
            id={content.id}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(content.val, editable) }}
            style={{
              color: filterBackgroundColor(resolvedBackgroundColor, textColor)
            }}
            isEmpty={isContentEmpty}
            {...(isDashboard
              ? {
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    try {
                      updateActiveElement(e);
                    } catch (error) {
                      handleEditError(error);
                    }
                  },
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta['accessorKey'],
                  'data-accessorkey': 'content',
                  'data-placeholder': '+',
                  editable: true
                }
              : {
                  role: 'textbox',
                  'aria-multiline': 'true',
                  'aria-label':
                    isContentEmpty || !content.val?.trim() ? 'Hero section description' : undefined,
                  'aria-describedby': title.id,
                  accessKey: contentAccessKey,
                  title: `Press Alt+${contentAccessKey} to focus this hero description`,
                  tabIndex: 0
                })}
          />

          <BtnInner role="group" aria-label={`Hero action area - ${buttonType}`}>
            {meta['url'] || type === 'delivery' ? (
              <ButtonInner>
                <DownloadStyled
                  textColor={textColor}
                  button={button}
                  meta={meta}
                  outerOnClick={() => {
                    if (editable) {
                      try {
                        // empty needs only for error
                      } catch (error) {
                        handleModalError(error);
                      }
                    }
                  }}
                  {...(!isDashboard && {
                    accessKey: buttonAccessKey,
                    title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                    'aria-describedby': `${title.id} ${content.id}`
                  })}
                />
                <ArrowsSvgStyled
                  iconName="icon_2"
                  backgroundColor={filterBackgroundColor(resolvedBackgroundColor, textColor)}
                />
              </ButtonInner>
            ) : (
              <ButtonInner>
                <StyledButton
                  to={purchaseElementId}
                  button={button}
                  meta={meta}
                  outerOnClick={() => {
                    if (editable) {
                      try {
                        // empty needs only for error
                      } catch (error) {
                        handleModalError(error);
                      }
                    }
                  }}
                  {...(!isDashboard && {
                    accessKey: buttonAccessKey,
                    title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                    'aria-describedby': `${title.id} ${content.id}`
                  })}
                />
                <ArrowsSvgStyled
                  iconName="icon_2"
                  backgroundColor={filterBackgroundColor(resolvedBackgroundColor, textColor)}
                />
              </ButtonInner>
            )}
          </BtnInner>
        </TitleWrapper>

        <RelativeWrapper>
          <ImgInner role="img" aria-label="Book preview with circular design">
            {bookMockup ? (
              <HoverableDropzoneStyled
                width={resize.width}
                height={resize.height}
                isSalePage
                css={imageStyles as never}
                disabled={!editable}
                resizeSelector={editable}
                enableLinks
                updateMeta={(value) => updateImageMeta(meta['accessorKey'], value, 'image_link')}
                handleResize={({ width, height }) => {
                  setResize({ width, height });
                  updateImageMeta(meta['accessorKey'], width, 'width');
                  updateImageMeta(meta['accessorKey'], height, 'height');
                }}
                onUpload={handleUpload}
                loading={isLoading}
                alreadyUploaded={Boolean(bookMockup)}
                imageFile={bookMockup}
                initialPosition={{
                  x: meta['x_1'] || 0,
                  y: meta['y_1'] || 0,
                  width: meta['d_width_1'],
                  height: meta['d_height_1']
                }}
                meta={meta}
                positionEnabled
                handlePosition={(width: number, height: number, x: number, y: number) => {
                  updateImageMeta(meta['accessorKey'], x, 'x_1');
                  updateImageMeta(meta['accessorKey'], y, 'y_1');
                  updateImageMeta(meta['accessorKey'], width, 'd_width_1');
                  updateImageMeta(meta['accessorKey'], height, 'd_height_1');
                }}
                imageAspectRatio={{
                  ratio: '3x4',
                  pixels:
                    resize.width && resize.height ? `${resize.width}x${resize.height}` : '600x800'
                }}
                ariaLabel="Book mockup image - drag and drop to upload new image">
                <BookMediaImg
                  src={bookMockup}
                  alt="Book mockup preview showing cover design with circular background"
                  width={resize.width || 500}
                  height={resize.height || 500}
                  priority
                />
              </HoverableDropzoneStyled>
            ) : (
              <StyledMockup
                mockup={mockup?.file}
                mockup_id={mockup?.id}
                cover_thumbnail={coverThumbnail}
                aria-label="Book mockup with cover thumbnail and circular design"
              />
            )}
          </ImgInner>

          <SvgBg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 703 703"
            aria-hidden="true"
            role="presentation"
            focusable="false">
            <circle className="cls-1" cx="351.5" cy="351.5" r="350" />
            <circle className="cls-3" cx="351.5" cy="351.5" r="306.34" />
            <circle fill={props.button?.color || COLOR_ORANGE} cx="46.5" cy="344.5" r="15.5" />
            <circle fill={props.button?.color || COLOR_ORANGE} cx="673.5" cy="203.5" r="15.5" />
            <ellipse
              fill={props.button?.color || COLOR_ORANGE}
              cx="625"
              cy="483.5"
              rx="15"
              ry="15.5"
            />
          </SvgBg>
        </RelativeWrapper>
      </StyledWrapper>

      <BenefitWrapper>
        <Wrapper>
          <Benefits role="region" aria-label="Key benefits and features">
            {benefits.map(({ content, icon, title }, index) => {
              const isTitleBenEmpty = checkTitleBenefits(index);
              const isContentBenEmpty = checkContentBenefits(index);
              const benefitAccessKey = generateAccessKey('hben', index);

              return (
                <BenefitItem
                  key={`${icon?.toString()}-${index}`}
                  role="article"
                  aria-label={`Benefit ${index + 1}: ${title?.val || 'Key feature'}`}
                  {...(!isDashboard && {
                    accessKey: benefitAccessKey,
                    title: `Press Alt+${benefitAccessKey} to focus benefit ${index + 1}`,
                    tabIndex: 0
                  })}>
                  <StyledSvgIcons
                    iconName={`icon_${index + 1}` as 'icon_1' | 'icon_2' | 'icon_3'}
                    backgroundColor={resolvedBackgroundColor}
                    aria-hidden="true"
                  />
                  <BenefitTxtInner>
                    <TitleBenefit
                      id={title?.id}
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(title?.val, editable) }}
                      style={{ color: filterBackgroundColor('#fff', textColor) }}
                      isEmpty={isTitleBenEmpty}
                      {...(isDashboard
                        ? {
                            onClick: (e: React.MouseEvent<HTMLElement>) => {
                              try {
                                updateActiveElement(e);
                              } catch (error) {
                                handleEditError(error);
                              }
                            },
                            onBlur: resetActiveElement,
                            'data-accessor-parent': meta['accessorKey'],
                            'data-accessorkey': 'title',
                            'data-benefits': true,
                            'data-placeholder': '+',
                            editable: true
                          }
                        : {
                            'aria-label': !title?.val?.trim()
                              ? `Benefit ${index + 1} title`
                              : undefined
                          })}
                    />
                    <TextBenefit
                      id={content?.id}
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(content?.val, editable) }}
                      style={{ color: filterBackgroundColor('#fff', textColor) }}
                      isEmpty={isContentBenEmpty}
                      {...(isDashboard
                        ? {
                            onClick: (e: React.MouseEvent<HTMLElement>) => {
                              try {
                                updateActiveElement(e);
                              } catch (error) {
                                handleEditError(error);
                              }
                            },
                            onBlur: resetActiveElement,
                            'data-accessor-parent': meta['accessorKey'],
                            'data-accessorkey': 'content',
                            'data-benefits': true,
                            'data-placeholder': '+',
                            editable: true
                          }
                        : {
                            'aria-label': !content?.val?.trim()
                              ? `Benefit ${index + 1} description`
                              : undefined,
                            'aria-describedby': title?.id
                          })}
                    />
                  </BenefitTxtInner>
                </BenefitItem>
              );
            })}
          </Benefits>
        </Wrapper>
      </BenefitWrapper>

      <WhiteInner
        style={{ opacity: meta?.['background_color']?.startsWith('base_color1') ? 0.5 : 1 }}
        aria-hidden="true"
        role="presentation">
        <RelativeWhite isWhite={meta?.['background_color']?.startsWith('background_color4')}>
          <div className="hero-white-v5 hero-white-v5_one"></div>
          <div className="hero-white-v5 hero-white-v5_two"></div>
          <div className="hero-white-v5 hero-white-v5_three"></div>
          <div className="hero-white-v5 hero-white-v5_four"></div>
          <div className="hero-white-v5 hero-white-v5_five"></div>
          <div className="hero-white-v5 hero-white-v5_six"></div>
        </RelativeWhite>
      </WhiteInner>

      <ColoredBg
        style={{ backgroundColor: resolvedBackgroundColor }}
        aria-hidden="true"
        role="presentation"
      />
    </HeroDetailed>
  );
};

V5.displayName = 'HeroDetailedV5';
